interface PasswordRequirementsProps {
  password: string
  passwordConfirmation?: string
  showConfirmation?: boolean
}

interface Requirement {
  label: string
  test: (password: string) => boolean
}

const requirements: Requirement[] = [
  { label: 'At least 6 characters', test: (p: string) => p.length >= 6 },
]

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export default function PasswordRequirements({
  password,
  passwordConfirmation,
  showConfirmation = false,
}: PasswordRequirementsProps) {
  const hasStartedTyping = password.length > 0
  const hasStartedConfirmation =
    passwordConfirmation !== undefined && passwordConfirmation.length > 0
  const passwordsMatch = password === passwordConfirmation && password.length > 0

  return (
    <div className="mt-2 space-y-1.5" role="status" aria-live="polite">
      {/* Password requirements */}
      <ul className="space-y-1">
        {requirements.map((req) => {
          const isMet = req.test(password)
          return (
            <li
              key={req.label}
              className={`flex items-center gap-1.5 text-xs transition-colors ${
                hasStartedTyping ? (isMet ? 'text-black' : 'text-black/40') : 'text-black/40'
              }`}
            >
              {hasStartedTyping && isMet ? (
                <CheckIcon className="w-3 h-3 flex-shrink-0" />
              ) : (
                <span className="w-3 h-3 flex-shrink-0 flex items-center justify-center text-[10px]">
                  ○
                </span>
              )}
              <span>{req.label}</span>
            </li>
          )
        })}
      </ul>

      {/* Password confirmation match indicator */}
      {showConfirmation && hasStartedConfirmation && (
        <div
          className={`flex items-center gap-1.5 text-xs transition-colors ${
            passwordsMatch ? 'text-black' : 'text-red-500'
          }`}
        >
          {passwordsMatch ? (
            <>
              <CheckIcon className="w-3 h-3 flex-shrink-0" />
              <span>Passwords match</span>
            </>
          ) : (
            <>
              <XIcon className="w-3 h-3 flex-shrink-0" />
              <span>Passwords do not match</span>
            </>
          )}
        </div>
      )}
    </div>
  )
}
